import type { JSX, ReactNode } from 'react';

import { Sheet, SheetContent, SheetDescription, SheetTitle } from './sheet.js';

/**
 * AppSheet — typed Sheet wrapper that makes the Radix a11y contract
 * impossible to forget. The contract: every Dialog/Sheet must have a
 * Title that the screen reader uses for `aria-labelledby`. Raw shadcn
 * Sheet leaves that to per-call discipline; this wrapper requires it
 * at compile time, so a future "I added a sheet and forgot the title"
 * regression is a TS error, not a quiet a11y violation.
 *
 * Pattern lifted from the way Linear / Vercel / Stripe ship Radix in
 * production: typed wrappers around the unsafe primitives, ESLint
 * `no-restricted-imports` blocking direct usage of the underlying
 * shadcn/Radix exports anywhere outside this folder.
 *
 * The title can be visually hidden via `titleHidden` for sheets whose
 * design has no header — Radix VisuallyHidden moves it off-screen
 * while keeping it in the accessibility tree.
 */
export interface AppSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Mandatory — sets aria-labelledby so screen readers announce the dialog. */
  title: string;
  /** Optional — adds aria-describedby for additional context. */
  description?: string;
  /**
   * Visually hide the title (still accessible to screen readers) for sheets
   * that have a custom header design or no header at all (e.g. nav drawer).
   */
  titleHidden?: boolean;
  side?: 'left' | 'right' | 'top' | 'bottom';
  className?: string;
  showCloseButton?: boolean;
  onOpenAutoFocus?: (event: Event) => void;
  onCloseAutoFocus?: (event: Event) => void;
  children: ReactNode;
}

export function AppSheet({
  open,
  onOpenChange,
  title,
  description,
  titleHidden = false,
  side = 'right',
  className,
  showCloseButton = true,
  onOpenAutoFocus,
  onCloseAutoFocus,
  children,
}: AppSheetProps): JSX.Element {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={side}
        className={className}
        showCloseButton={showCloseButton}
        onOpenAutoFocus={onOpenAutoFocus}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <SheetTitle className={titleHidden ? 'sr-only' : undefined}>
          {title}
        </SheetTitle>
        {description !== undefined && description.length > 0 && (
          <SheetDescription className={titleHidden ? 'sr-only' : undefined}>
            {description}
          </SheetDescription>
        )}
        {children}
      </SheetContent>
    </Sheet>
  );
}
